import { useState, type ComponentProps } from "react";
import { cn } from "~/lib/utils";

export const OTP_LENGTH = 6;

export function OtpInput({
  value,
  onChange,
  onComplete,
  invalid,
  pending,
  className,
  ...props
}: Omit<ComponentProps<"input">, "value" | "onChange" | "type" | "readOnly"> & {
  value: string;
  onChange: (code: string) => void;
  onComplete: (code: string) => void;
  invalid?: boolean;
  pending?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const activeIndex = Math.min(value.length, OTP_LENGTH - 1);

  function handleChange(raw: string) {
    const code = raw.replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(code);
    if (code.length === OTP_LENGTH) onComplete(code);
  }

  return (
    <div className={cn("relative", invalid && "shake", className)}>
      <div className={cn("grid grid-cols-6 gap-2 transition-opacity", pending && "opacity-50")} aria-hidden>
        {Array.from({ length: OTP_LENGTH }, (_, i) => {
          const digit = value[i] ?? "";
          const active = focused && !pending && i === activeIndex;
          return (
            <div
              key={i}
              className={cn(
                "bg-surface tabular flex h-14 items-center justify-center rounded-lg border text-[24px] font-semibold transition-colors",
                invalid ? "border-destructive" : active ? "border-foreground ring-ring/40 ring-2" : "border-hairline",
              )}
            >
              {digit}
              {active && !digit && <span className="bg-foreground caret-blink h-6 w-[1.5px]" />}
            </div>
          );
        })}
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d*"
        value={value}
        readOnly={pending}
        aria-invalid={invalid || undefined}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 h-full w-full cursor-text rounded-lg border-0 bg-transparent text-[16px] tracking-[-0.5em] text-transparent caret-transparent outline-none selection:bg-transparent"
        {...props}
      />
    </div>
  );
}
